/**
 * DNS zone record editor. When `isLive=true` (TPP REST account) writes are
 * pushed to the live registry via the REST DNS API; otherwise records are
 * kept in our DB as manual reference (legacy TPP has no zone-file API).
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileText, Loader2, Pencil, Plus, RefreshCw, Trash2, X, Zap } from "lucide-react";

const TYPES = ["A", "AAAA", "CNAME", "MX", "TXT", "NS", "SRV", "CAA", "PTR"];
const empty = { record_type: "A", host: "", value: "", ttl: "", priority: "", notes: "" };

export function ZoneRecordsEditor({ domain, readOnly = false, isLive = false, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [records, setRecords] = useState(domain.zone_records || []);
  const [loading, setLoading] = useState(false);

  // Zone records are cached in the local DB along with the rest of the
  // domain document; we only re-fetch from the live TPP DNS API when the
  // user clicks Reload (or after they add/edit/delete a record).
  const reload = useCallback(async () => {
    if (!isLive) { setRecords(domain.zone_records || []); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/domains/${domain.domain_id}/zone-records`);
      setRecords(data?.records || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "DNS fetch failed");
    } finally { setLoading(false); }
  }, [isLive, domain.domain_id]);

  // On mount / when domain changes: show cached records. Do NOT hit TPP.
  useEffect(() => { setRecords(domain.zone_records || []); }, [domain.zone_records, domain.domain_id]);

  const save = async () => {
    if (!draft.host.trim() || !draft.value.trim()) {
      toast.error("Host and value are required"); return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/domains/${domain.domain_id}/zone-records/${editingId}`, draft);
      } else {
        await api.post(`/domains/${domain.domain_id}/zone-records`, draft);
      }
      toast.success("Zone record saved");
      setAdding(false); setEditingId(null); setDraft(empty);
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Delete ${rec.record_type} record for "${rec.host}"?${isLive ? "\n\nThis is a LIVE change at the TPP registrar." : ""}`)) return;
    try {
      await api.delete(`/domains/${domain.domain_id}/zone-records/${rec.record_id}`);
      toast.success("Record removed");
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const beginEdit = (rec) => {
    setEditingId(rec.record_id); setAdding(true);
    setDraft({
      record_type: rec.record_type, host: rec.host, value: rec.value,
      ttl: rec.ttl || "", priority: rec.priority || "", notes: rec.notes || "",
    });
  };

  return (
    <div className="space-y-2" data-testid={`zone-records-${domain.domain_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="label-overline flex items-center gap-1.5">
          {isLive ? <Zap className="h-3 w-3 text-emerald-600" /> : <FileText className="h-3 w-3" />}
          Zone records {isLive ? "(live at TPP)" : "(manual reference)"}
          {records.length > 0 && <span className="text-zinc-400">({records.length})</span>}
        </div>
        <div className="flex items-center gap-1.5">
          {isLive && (
            <button type="button" onClick={reload}
              className="text-[10px] font-mono text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1"
              data-testid={`zone-records-reload-${domain.domain_id}`}>
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Reload
            </button>
          )}
          {!readOnly && !adding && (
            <button type="button"
              onClick={() => { setAdding(true); setEditingId(null); setDraft(empty); }}
              className="text-[10px] font-mono text-[#002FA7] hover:underline inline-flex items-center gap-1"
              data-testid={`zone-record-add-${domain.domain_id}`}>
              <Plus className="h-3 w-3" /> Add record
            </button>
          )}
        </div>
      </div>
      <p className="text-[10px] text-zinc-500">
        {isLive
          ? "Cached from TPP — click Reload for the latest live copy. Add/edit/delete pushes changes live to the TPP DNS servers. TTLs are in seconds."
          : "The legacy TPP API doesn't expose zone files — keep DNS records here for reference."}
      </p>

      {records.length === 0 && !adding ? (
        <div className="text-[11px] text-zinc-400 italic">No zone records recorded yet.</div>
      ) : (
        <div className="border border-zinc-200 rounded-sm overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-zinc-50 text-zinc-500 font-mono uppercase text-[9px]">
              <tr>
                <th className="text-left px-2 py-1">Type</th>
                <th className="text-left px-2 py-1">Host</th>
                <th className="text-left px-2 py-1">Value</th>
                <th className="text-left px-2 py-1">TTL</th>
                <th className="text-left px-2 py-1">Prio</th>
                <th className="text-left px-2 py-1">Notes</th>
                {!readOnly && <th className="px-2 py-1" />}
              </tr>
            </thead>
            <tbody>
              {records.map((rec) => (
                <tr key={rec.record_id} className="border-t border-zinc-100"
                  data-testid={`zone-record-row-${rec.record_id}`}>
                  <td className="px-2 py-1 font-mono font-bold">{rec.record_type}</td>
                  <td className="px-2 py-1 font-mono">{rec.host}</td>
                  <td className="px-2 py-1 font-mono break-all max-w-[280px]">{rec.value}</td>
                  <td className="px-2 py-1 font-mono">{rec.ttl || "—"}</td>
                  <td className="px-2 py-1 font-mono">{rec.priority || "—"}</td>
                  <td className="px-2 py-1 text-zinc-500">{rec.notes || "—"}</td>
                  {!readOnly && (
                    <td className="px-2 py-1 whitespace-nowrap text-right">
                      <button type="button" onClick={() => beginEdit(rec)}
                        className="text-zinc-500 hover:text-[#002FA7] mr-2"
                        data-testid={`zone-record-edit-${rec.record_id}`}>
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button type="button" onClick={() => remove(rec)}
                        className="text-zinc-500 hover:text-red-600"
                        data-testid={`zone-record-delete-${rec.record_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && !readOnly && (
        <div className="border border-[#002FA7]/20 rounded-sm p-2 bg-[#002FA7]/[0.03] space-y-1.5"
          data-testid="zone-record-form">
          <div className="grid grid-cols-12 gap-1.5">
            <select value={draft.record_type}
              onChange={(e) => setDraft({ ...draft, record_type: e.target.value })}
              className="col-span-2 h-8 rounded-sm border border-zinc-200 text-xs font-mono px-1 bg-white"
              data-testid="zone-record-type">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <Input value={draft.host} placeholder="host / @"
              onChange={(e) => setDraft({ ...draft, host: e.target.value })}
              className="col-span-3 h-8 rounded-sm text-xs font-mono" data-testid="zone-record-host" />
            <Input value={draft.value} placeholder="value / target"
              onChange={(e) => setDraft({ ...draft, value: e.target.value })}
              className="col-span-4 h-8 rounded-sm text-xs font-mono" data-testid="zone-record-value" />
            <Input value={draft.ttl} placeholder="TTL"
              onChange={(e) => setDraft({ ...draft, ttl: e.target.value })}
              className="col-span-1 h-8 rounded-sm text-xs font-mono" />
            <Input value={draft.priority} placeholder="Prio"
              onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
              className="col-span-2 h-8 rounded-sm text-xs font-mono" />
          </div>
          <Input value={draft.notes} placeholder="Notes (e.g. points to Kinsta, SPF for SendGrid…)"
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            className="h-8 rounded-sm text-xs" data-testid="zone-record-notes" />
          <div className="flex items-center gap-1.5">
            <Button size="sm" onClick={save} disabled={busy}
              className="rounded-sm h-7 bg-[#002FA7] hover:bg-[#001f70] text-white"
              data-testid="zone-record-save">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : (editingId ? "Save changes" : "Add record")}
            </Button>
            <Button size="sm" variant="ghost" className="rounded-sm h-7"
              onClick={() => { setAdding(false); setEditingId(null); setDraft(empty); }}>
              <X className="h-3 w-3 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function expiryBadge(expiry) {
  if (!expiry) return { label: "—", cls: "bg-zinc-50 text-zinc-500 border-zinc-200" };
  const days = Math.floor((new Date(expiry) - new Date()) / 86400000);
  if (days < 0) return { label: `expired ${Math.abs(days)}d ago`, cls: "bg-red-50 text-red-700 border-red-200" };
  if (days <= 30) return { label: `${days}d left`, cls: "bg-red-50 text-red-700 border-red-200" };
  if (days <= 90) return { label: `${days}d left`, cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: `${days}d left`, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
}
